<template>
  <div class="floor-share-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('floorShare.title')" :subtitle="$t('floorShare.page.subtitle')">
      <template #extra>
        <el-button v-if="floorShareInfo.conditions.floorId" type="primary" icon="el-icon-plus"
          @click="openAddFloorShareModal">
          {{ $t('common.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel：楼层选择 -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <select-community-floor ref="selectCommunityFloor" @floor-change="handleFloorChange" />
        </el-col>
      </el-row>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="listFloorShares">
        <template #left>
          <span class="ds-panel__title">{{ $t('floorShare.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="floorShareInfo.floorShares" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('floorShare.page.empty')" @retry="listFloorShares">
        <el-table-column prop="fsmId" :label="$t('floorShare.id')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.fsmId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="floorNum" :label="$t('floorShare.floor')" align="center" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.floorNum || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="meterTypeName" :label="$t('floorShare.meterType')" align="center" width="120">
          <template slot-scope="scope">
            {{ scope.row.meterTypeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="meterNum" :label="$t('floorShare.meterNum')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.meterNum || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="shareTypeName" :label="$t('floorShare.shareType')" align="center" width="110">
          <template slot-scope="scope">
            {{ scope.row.shareTypeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="formulaValue" :label="$t('floorShare.formula')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.formulaValue || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="configName" :label="$t('floorShare.feeItem')" align="left" min-width="140"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.configName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="sharePrice" :label="$t('floorShare.feePrice')" align="right" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.sharePrice) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="curDegree" :label="$t('floorShare.degree')" align="right" width="120">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatNumber(scope.row.curDegree) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="curReadingTime" :label="$t('floorShare.readingTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.curReadingTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('floorShare.createTime')" align="center" width="180">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="150" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="openEditFloorShareModal(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="openDeleteFloorShareModal(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('floorShare.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <add-floor-share ref="addFloorShare" @success="handleSuccess" />
    <edit-floor-share ref="editFloorShare" @success="handleSuccess" />
    <delete-floor-share ref="deleteFloorShare" @success="handleSuccess" />
  </div>
</template>

<script>
import { listFloorShareMeter } from '@/api/fee/floorShareApi'
import SelectCommunityFloor from '@/components/report/selectCommunityFloor'
import AddFloorShare from '@/components/fee/addFloorShare'
import EditFloorShare from '@/components/fee/editFloorShare'
import DeleteFloorShare from '@/components/fee/deleteFloorShare'
import { getCommunityId } from '@/api/community/communityApi'
import { formatDateTimeSec, formatMoney, formatNumber } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'FloorShareList',
  components: {
    SelectCommunityFloor,
    AddFloorShare,
    EditFloorShare,
    DeleteFloorShare,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      floorShareInfo: {
        floorShares: [],
        conditions: {
          floorId: '',
          communityId: ''
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.floorShareInfo.conditions.communityId = getCommunityId()
    setTimeout(() => {
      this.$refs.selectCommunityFloor.open({
        callBack: this.handleFloorChange
      })
    }, 1000)
  },
  methods: {
    async listFloorShares() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.page.current,
          row: this.page.size,
          floorId: this.floorShareInfo.conditions.floorId,
          communityId: this.floorShareInfo.conditions.communityId
        }
        const { data, total } = await listFloorShareMeter(params)
        this.floorShareInfo.floorShares = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('获取楼层公摊表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('floorShare.page.loadFailed')
        this.$message.error(this.$t('floorShare.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleFloorChange(floor) {
      this.floorShareInfo.conditions.floorId = floor.floorId
      this.floorShareInfo.conditions.floorNum = floor.floorNum
      this.page.current = 1
      this.listFloorShares()
    },
    openAddFloorShareModal() {
      this.$refs.addFloorShare.open({
        floorId: this.floorShareInfo.conditions.floorId,
        floorNum: this.floorShareInfo.conditions.floorNum
      })
    },
    openEditFloorShareModal(row) {
      this.$refs.editFloorShare.open(row)
    },
    openDeleteFloorShareModal(row) {
      this.$refs.deleteFloorShare.open(row)
    },
    handleSuccess() {
      this.listFloorShares()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.listFloorShares()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.listFloorShares()
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    },
    formatMoney(value) {
      return formatMoney(value)
    },
    formatNumber(value) {
      return formatNumber(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.floor-share-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
